import React, { useContext, useEffect, useState } from "react";
import "./Product.css";
import { Link, useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import toast from "react-hot-toast";
import { AuthContext } from "../../Context/AuthContextProvider";
import BuyNow from "../BuyNow/BuyNow";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import { post } from "../../utils/api";

Marketplace_SinglePrice.propTypes = {
  product: PropTypes.object.isRequired,
};

function Marketplace_SinglePrice({ product }) {
  const {
    isAuth,
    loginUser,
    EligibilityVerification,
    setDomainUnderConsiderationAndPersist,
  } = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enquiryMessage, setEnquiryMessage] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [hasExistingInquiry, setHasExistingInquiry] = useState(false);
  const navigate = useNavigate();

  // Scroll to top when product changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [product]);

// Check if current user is the owner of this product or already has an inquiry
useEffect(() => {
  if (loginUser && product) {
    const userId = loginUser._id || loginUser.id;
    const userName = loginUser.name || loginUser.username;

    // Check ACTUAL ownership fields from your schema
    const ownerCheck = userId && (
      userId.toString() === product.submittedBy?.toString() ||
      userId.toString() === product.creator?.creatorId?.toString() ||
      userName === product.creator?.creatorName
    );

    // Check if this user already submitted an inquiry
    const existingInquiry = product.inquiries?.some((inq) => {
      const inquiryUserId = inq.userId?._id || inq.userId;
      return inquiryUserId?.toString() === userId?.toString();
    });

    setIsOwner(ownerCheck);
    setHasExistingInquiry(existingInquiry);
  } else {
    setIsOwner(false);
    setHasExistingInquiry(false);
  }
}, [loginUser, product]);

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();

    if (!isAuth) {
      toast.error("Please login to send an enquiry.");
      navigate("/login");
      return;
    }

    if (!enquiryMessage.trim()) {
      toast.error("Please enter a message before submitting.");
      return;
    }

    // Prevent user from enquiring about their own product
    if (isOwner) {
      toast.error("You cannot enquire about your own product.");
      return;
    }

    // Prevent duplicate inquiries
    if (hasExistingInquiry) {
      toast.error("You have already sent an inquiry for this product.");
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await post(`/domain/create-inquiry/${product._id}`, {
        userId: loginUser._id || loginUser.id,
        userName: loginUser.name || loginUser.username,
        userEmail: loginUser.email_id || loginUser.email,
        message: enquiryMessage,
        contactMethod: "contact_form",
      });

      if (response.success) {
        toast.success("Your enquiry has been sent successfully! The seller will respond soon.");
        setEnquiryMessage("");
        setHasExistingInquiry(true);
      }
    } catch (error) {
      if (error.response?.status === 403 || error.response?.data?.error?.includes("own product")) {
        toast.error("You cannot enquire about your own product.");
        setIsOwner(true);
      } else if (error.response?.data?.error?.includes("already have a pending inquiry")) {
        toast.error("You already have a pending inquiry for this product.");
        setHasExistingInquiry(true);
      } else {
        toast.error("Failed to send enquiry. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      id="marketplace_product_detail"
      className="w-full p-4 lg:p-6 lg:mb-0 search_part relative col-span-12 lg:col-span-4"
    >
      <div className="p-4 bg-white shadow-lg rounded-xl border border-gray-200">
        <h2 className="text-[26px] font-bold text-[#101c4c] mb-2">
          {product.name}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {product.premium
            ? "This is a premium .gold domain available for direct purchase."
            : "A unique .gold domain ideal for your digital brand."}
        </p>

        <div className="flex justify-between items-center mb-4">
          <span className="text-gray-700 text-lg font-medium">Price:</span>
          <span className="text-[#b99653] font-bold text-2xl">
            ₹ {product.price ? product.price.toLocaleString() : "—"}
          </span>
        </div>

        <div className="flex flex-col gap-3 mt-4">
          {!isOwner && (
            <Link to={`/Marketplace-checkout/${product._id}`}>
              <button className="custom-btn main-button w-full">Buy Now</button>
            </Link>
          )}

          <hr />
          
          {/* Show different UI based on user status */}
          {isOwner ? (
            // Owner sees this
            <div className="mt-2 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm font-medium flex items-center gap-2">
                <span className="text-lg">👤</span>
                This is your listing. You can manage it from your dashboard.
              </p>
              <Link 
                to="/Dashboard/MySubmissions" 
                className="text-blue-600 hover:text-blue-800 text-sm font-semibold underline mt-2 inline-block"
              >
                Go to My Submissions →
              </Link>
            </div>
          ) : hasExistingInquiry ? (
            // User who already sent inquiry sees this
            <div className="mt-2 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
              <p className="text-green-800 text-sm font-medium flex items-center gap-2">
                <span className="text-lg">✅</span>
                You've already sent an inquiry for this domain. The seller will respond to your email.
              </p>
            </div>
          ) : (
            // Other users see the inquiry form
            <div className="mt-2">
              <h3 className="text-[18px] font-semibold text-[#101c4c] mb-2">
                Interested but not ready to buy?
              </h3>
              <form onSubmit={handleEnquirySubmit}>
                <TextField
                  id="enquiryMessage"
                  label="Send an enquiry"
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  value={enquiryMessage}
                  onChange={(e) => setEnquiryMessage(e.target.value)}
                  placeholder="Hi, I'm interested in this domain. Can you provide more details?"
                  sx={{
                    marginTop: "6px",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "grey" },
                      "&:hover fieldset": { borderColor: "#b99653" },
                      "&.Mui-focused fieldset": { borderColor: "#b99653" },
                    },
                    "& input, & textarea": {
                      color: "#101c4c",
                    },
                    "& label": {
                      color: "#777",
                    },
                    "& label.Mui-focused": {
                      color: "#b99653",
                    },
                  }}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="custom-btn main-button w-full mt-2"
                >
                  {isSubmitting ? "Sending..." : "Send Enquiry"}
                </button>
              </form>
            </div>
          )}
        </div>

        <div className="mt-6 text-xs text-gray-500">
          <p>🔒 Secure checkout powered by Cashfree</p>
          <p>💳 Supports all major cards, UPI, and Net Banking</p>
        </div>
      </div>
    </div>
  );
}

export default Marketplace_SinglePrice;